'use client';

import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

/**
 * Avatar — Radix Avatar with initials fallback.
 *
 * Sizes: 24 | 32 | 40 | 48 | 64 (pixel dimensions).
 * Ring variant: `status="online" | "offline" | "busy" | "none"` adds a
 * colored ring for presence.
 *
 * Fallback: `initialsFrom("Fahad Shabir")` → "FS" and picks a deterministic
 * background color from a small palette using a hash. This gives every user
 * a stable identity even before their avatar image loads.
 */

const sizeMap = { 24: 24, 32: 32, 40: 40, 48: 48, 64: 64 } as const;
type AvatarSize = keyof typeof sizeMap;

const containerVariants = cva(
  [
    "relative inline-flex items-center justify-center",
    "overflow-hidden rounded-full",
    "bg-chrome-panel text-ink-primary select-none shrink-0",
    "font-sans font-semibold",
  ].join(" "),
  {
    variants: {
      size: {
        24: "size-6 text-caption-12",
        32: "size-8 text-caption-12",
        40: "size-10 text-body-14",
        48: "size-12 text-body-14",
        64: "size-16 text-body-16",
      },
      status: {
        none: "",
        online: "ring-2 ring-status-success ring-offset-2 ring-offset-chrome-surface",
        busy: "ring-2 ring-status-error ring-offset-2 ring-offset-chrome-surface",
        offline: "ring-2 ring-ink-muted ring-offset-2 ring-offset-chrome-surface",
      },
    },
    defaultVariants: { size: 40, status: "none" },
  },
);

// Small palette rotated by string hash — deterministic per-name identity.
const fallbackPalette = [
  "bg-[color-mix(in_oklab,var(--accent-violet)_18%,transparent)] text-accent-violet",
  "bg-[color-mix(in_oklab,var(--status-success)_18%,transparent)] text-status-success",
  "bg-[color-mix(in_oklab,var(--status-warn)_18%,transparent)] text-status-warn",
  "bg-[color-mix(in_oklab,var(--status-error)_18%,transparent)] text-status-error",
  "bg-chrome-panel text-ink-primary",
];

/** Extract up to 2 upper-case initials from a name string. */
export function initialsFrom(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return (first + last).toUpperCase() || "?";
}

/** Cheap deterministic hash for palette selection. */
function stringHash(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function paletteColorFor(name: string): string {
  return fallbackPalette[stringHash(name) % fallbackPalette.length]!;
}

export interface AvatarProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
      "children"
    >,
    VariantProps<typeof containerVariants> {
  /** Image src. Falls back to initials if missing or if the image errors. */
  src?: string | null;
  /** Alt text for the image, also drives the initials fallback. */
  name: string;
  /** Optional custom fallback content (overrides initials). */
  fallback?: React.ReactNode;
}

export const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(function Avatar(
  { className, src, name, size, status, fallback, ...props },
  ref,
) {
  return (
    <AvatarPrimitive.Root
      ref={ref}
      data-slot="avatar"
      data-size={size ?? 40}
      className={cn(containerVariants({ size, status, className }))}
      {...props}
    >
      {src ? (
        <>
          <AvatarPrimitive.Image
            src={src}
            alt={name}
            className="aspect-square size-full object-cover"
          />
          <AvatarPrimitive.Fallback
            delayMs={400}
            className={cn(
              "flex size-full items-center justify-center",
              paletteColorFor(name),
            )}
          >
            {fallback ?? initialsFrom(name)}
          </AvatarPrimitive.Fallback>
        </>
      ) : (
        // No image intended — render initials directly to avoid Radix's
        // fallback delay (which relies on Image error events that never fire
        // when no Image is mounted). This matches shadcn/ui behavior.
        <span
          data-slot="avatar-fallback"
          className={cn(
            "flex size-full items-center justify-center",
            paletteColorFor(name),
          )}
        >
          {fallback ?? initialsFrom(name)}
        </span>
      )}
    </AvatarPrimitive.Root>
  );
});
Avatar.displayName = "Avatar";

export { containerVariants as avatarVariants };
